import { useState } from "react";
import { useAuthStore } from "../store/authStore";

interface UseProtectedAdminActionOptions {
	onError?: (error: string) => void;
	onSuccess?: () => void;
}

/**
 * Hook per eseguire azioni protette che richiedono verifica admin.
 *
 * Usato per:
 * - Create/Update/Delete prodotti
 * - Update ordini
 * - Qualsiasi operazione che richiede verifica admin server-side
 *
 * Implementa:
 * - Verifica admin prima dell'azione
 * - Usa cache (TTL 15 minuti) per prestazioni
 * - Error handling
 * - Loading state
 * - Audit logging lato server
 *
 * @example
 * const { executeProtectedAction, loading, error } = useProtectedAdminAction();
 *
 * const handleDeleteProduct = async (productId: string) => {
 *   await executeProtectedAction(async () => {
 *     await supabase.from('products').delete().eq('id', productId);
 *   });
 * };
 */
export const useProtectedAdminAction = (
	options?: UseProtectedAdminActionOptions,
) => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const { verifyAdminRole } = useAuthStore();

	const executeProtectedAction = async <T>(
		action: () => Promise<T>,
	): Promise<T | null> => {
		try {
			setLoading(true);
			setError(null);

			// Verifica admin (usa cache se valido)
			const adminVerification = await verifyAdminRole();

			if (!adminVerification.isAdmin) {
				const errorMsg =
					"Accesso negato: solo amministratori possono eseguire questa azione";
				setError(errorMsg);
				options?.onError?.(errorMsg);
				return null;
			}

			// Esegui l'azione protetta
			const result = await action();

			setError(null);
			options?.onSuccess?.();

			return result;
		} catch (err) {
			const errorMsg =
				err instanceof Error ? err.message : "Errore sconosciuto";
			setError(errorMsg);
			options?.onError?.(errorMsg);
			console.error("Protected action error:", err);
			return null;
		} finally {
			setLoading(false);
		}
	};

	const clearError = () => setError(null);

	return {
		executeProtectedAction,
		loading,
		error,
		clearError,
	};
};
